import React from 'react';
import { useState } from 'react';
import { useNavigate, Link, Routes, Route } from 'react-router-dom';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import api from '../../api/Base';
import Alert from '../../component/Alert';
import { PasswordField } from '../../components/forms';
import Breadcrumb from "../../container/breadcrumb";
import HeaderActions from "../../container/header_actions";
import ConditionalRender from "../../helpers/ConditionalRender";

function ChangePasswordForm() {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [invalidCurrentPassword, setInvalidCurrentPassword] = useState('');
    const [invalidNewPassword, setInvalidNewPassword] = useState('');
    const [passwordMismatch, setPasswordMismatch] = useState(false);

    const navigate = useNavigate();

    const validateForm = () => {
        let isValid = true;
        setInvalidCurrentPassword('');
        setInvalidNewPassword('');
        setPasswordMismatch(false);
        setErrorMessage('');

        if (!currentPassword) {
            setInvalidCurrentPassword('is-invalid');
            isValid = false;
        }

        if (!newPassword) {
            setInvalidNewPassword('is-invalid');
            isValid = false;
        } else if (newPassword.length < 8) {
            setInvalidNewPassword('is-invalid');
            isValid = false;
        }

        if (newPassword !== confirmPassword) {
            setPasswordMismatch(true);
            isValid = false;
        }

        if (currentPassword === newPassword) {
            setErrorMessage('New password must be different from current password');
            isValid = false;
        }

        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const passwordData = [{
                currentPassword,
                newPassword
            }];

            const response = await api.patch('login/password', passwordData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const responseData = Array.isArray(response.data) ? response.data[0] : response.data;
            const backendError = responseData?.error;

            if (response.status === 200 && !backendError) {
                setSuccessMessage('Password changed successfully!');
                // Clear form
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                // Redirect to home after a delay
                setTimeout(() => {
                    navigate('/');
                }, 2000);
            } else if (backendError) {
                const isCurrentPasswordError =
                    backendError.toLowerCase().includes('current password') ||
                    backendError.toLowerCase().includes('incorrect');
                if (isCurrentPasswordError) {
                    setInvalidCurrentPassword('is-invalid');
                    setErrorMessage('The current password is incorrect or does not match.');
                } else {
                    setErrorMessage(backendError);
                }
            }
        } catch (error: any) {
            console.error('Password change error:', error);
            const status = error.response?.status;
            const data = error.response?.data;
            const backendError = Array.isArray(data) ? data[0]?.error : data?.error;

            if (status === 403 || backendError?.toLowerCase().includes('current password')) {
                setInvalidCurrentPassword('is-invalid');
                setErrorMessage('The current password is incorrect or does not match.');
            } else if (backendError) {
                setErrorMessage(backendError);
            } else {
                setErrorMessage('Failed to change password. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/user-settings');
    };

    return (
        <div className="row justify-content-center">
            <div className="col-md-6 col-lg-4">
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-key fa-fw" aria-hidden="true"></i>
                        Change Password
                    </div>

                    <div className="card-body">
                        {errorMessage && (
                        <Alert
                            message={errorMessage}
                            heading="Error"
                            variant="alert-danger"
                        />
                    )}

                    {successMessage && (
                        <Alert
                            message={successMessage}
                            heading="Success"
                            variant="alert-success"
                        />
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label htmlFor="currentPassword" className="form-label">
                                Current Password *
                            </label>
                            <PasswordField
                                id="currentPassword"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                required
                                dataLpIgnore
                                standalone
                                error={invalidCurrentPassword === 'is-invalid' ? 'Current password is required' : undefined}
                                className={invalidCurrentPassword}
                            />
                        </div>

                        <div className="mb-3">
                            <label htmlFor="newPassword" className="form-label">
                                New Password *
                            </label>
                            <PasswordField
                                id="newPassword"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                dataLpIgnore
                                standalone
                                error={invalidNewPassword === 'is-invalid' ? (newPassword.length < 8 ? 'Password must be at least 8 characters long' : 'New password is required') : undefined}
                                className={invalidNewPassword}
                            />
                        </div>

                        <div className="mb-3">
                            <label htmlFor="confirmPassword" className="form-label">
                                Confirm New Password *
                            </label>
                            <PasswordField
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                dataLpIgnore
                                standalone
                                error={passwordMismatch ? 'Passwords do not match' : undefined}
                                className={passwordMismatch ? 'is-invalid' : ''}
                            />
                        </div>

                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="btn btn-secondary flex-fill"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary flex-fill"
                                disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                            >
                                {loading ? 'Changing...' : 'Change Password'}
                            </button>
                        </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ChangePassword(props: {
    toggleMenu: Function,
    applyPeriod: Function
}) {
    return (
        <Routes>
            <Route path="/" element={
                <React.Fragment>
                    <div id="content">
                        <Helmet>
                            <title>Change Password - DBmarlin</title>
                            <meta name="description" content="Change your password"/>
                        </Helmet>

                        <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                            <Breadcrumb heading="Change Password">
                                <Link to="/user-settings">User Settings</Link>
                                <span>Change Password</span>
                            </Breadcrumb>
                            <HeaderActions toggleMenu={props.toggleMenu} refresh={() => {
                            }}/>
                        </div>

                        <div className="loader">
                            <ConditionalRender if={false}>
                                <div className="bar"></div>
                            </ConditionalRender>
                        </div>

                        <ChangePasswordForm />
                    </div>
                </React.Fragment>
            }/>
        </Routes>
    );
}

ChangePassword.propTypes = {
    toggleMenu: PropTypes.func.isRequired,
    applyPeriod: PropTypes.func.isRequired
};

export default ChangePassword;